// ?use_ios_ui — twin of iOS 0.7.0 CurrentWheelSizeOverlay: "What size
// wheels are on your car?", asked on an opaque white card that covers the
// poll and the download, so the answer costs time already being spent.
// The slider moves only a DRAFT — a slider always rests on some value,
// which would read as answered. Select writes it; Skip writes nothing.

import { useState } from 'react'
import { PanelSlider } from '@/components/ios-ui/panel-slider/panel-slider'
import { IOS_UI_FONT, IOS_UI_PALETTE } from '@/components/ios-ui/ios-ui-palette/ios-ui-palette'
import { CURRENT_WHEEL_SIZE_RANGE, type TCurrentWheelSize } from '@/models/current-wheel-size'

/** The middle of 13–30 and the commonest answer. */
const OPENING_SIZE_IN = 18
/** Width of the stacked column, so slider and buttons read as one. */
const CARD_MEASURE_PX = 200

interface CurrentWheelSizeCardProps {
  /** Non-null only when shown again after a failed load was retried. */
  answer: TCurrentWheelSize | null
  onAnswer: (inches: number) => void
  onDismiss: () => void
}

export function CurrentWheelSizeCard({ answer, onAnswer, onDismiss }: CurrentWheelSizeCardProps) {
  const [inches, setInches] = useState(answer?.inches ?? OPENING_SIZE_IN)
  return (
    <div
      data-testid="ios-size-question"
      style={{
        position: 'fixed',
        inset: 0,
        background: '#FFFFFF',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontFamily: IOS_UI_FONT,
        pointerEvents: 'auto',
        zIndex: 30,
      }}
    >
      <div style={{ padding: 32, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
        <div data-testid="ios-size-question-title" style={{ fontSize: 17, fontWeight: 600, color: '#000' }}>
          What size wheels are on your car?
        </div>
        <div style={{ fontSize: 12, color: 'rgba(60, 60, 67, 0.6)' }}>We use this to blend the new wheel over your own.</div>
        <div data-testid="ios-size-question-value" style={{ marginTop: 4, fontSize: 34, fontWeight: 600, color: '#000', fontVariantNumeric: 'tabular-nums' }}>
          {`${inches}"`}
        </div>
        <div style={{ width: CARD_MEASURE_PX, display: 'flex' }}>
          <PanelLightSlider value={inches} onChange={setInches} />
        </div>
        <button
          type="button"
          data-testid="ios-size-question-select"
          onClick={() => {
            onAnswer(inches)
            onDismiss()
          }}
          style={{
            marginTop: 8,
            width: CARD_MEASURE_PX,
            height: 44,
            border: 'none',
            borderRadius: 22,
            background: IOS_UI_PALETTE.accent,
            color: IOS_UI_PALETTE.onAccent,
            fontSize: 17,
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          Select
        </button>
        <button
          type="button"
          data-testid="ios-size-question-skip"
          onClick={onDismiss}
          style={{ border: 'none', background: 'transparent', color: 'rgba(60, 60, 67, 0.6)', fontSize: 15, padding: 8, cursor: 'pointer' }}
        >
          Skip
        </button>
      </div>
    </div>
  )
}

/** The panel slider in the light card's accent. */
function PanelLightSlider({ value, onChange }: { value: number; onChange: (inches: number) => void }) {
  return (
    <PanelSlider
      testId="ios-size-question-slider"
      ariaLabel="Current wheel size"
      value={value}
      min={CURRENT_WHEEL_SIZE_RANGE.min}
      max={CURRENT_WHEEL_SIZE_RANGE.max}
      step={1}
      onChange={onChange}
      accent={IOS_UI_PALETTE.accent}
      trackColor="rgba(120, 120, 128, 0.2)"
      thumb="light"
    />
  )
}
